import { useState } from 'react';
import { Card, Table, Button, Collapse, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LogRankTestResult } from '../../../utils/statistics/survival';

interface CalculationStepsViewProps {
  logRankTest: LogRankTestResult;
}

/**
 * ログランク検定の計算過程を表示するコンポーネント
 */
export default function CalculationStepsView({ logRankTest }: CalculationStepsViewProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true); // デフォルトで展開

  // 計算過程が利用できない場合のチェック
  if (
    !logRankTest.calculationSteps ||
    !logRankTest.groupNames ||
    logRankTest.calculationSteps.length === 0
  ) {
    // 計算過程が利用できない場合でも、カードは表示する（デバッグ情報付き）
    return (
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <span>{t('keisuke.survival.calculationSteps')}</span>
            <small className="text-muted">
              (
              {t('keisuke.survival.calculationStepsNotAvailable')}
              )
            </small>
          </div>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            {t('keisuke.survival.calculationStepsNotAvailable')}
            <br />
            <small>
              calculationSteps:{' '}
              {logRankTest.calculationSteps
                ? t('keisuke.survival.debugPresence', {
                    count: logRankTest.calculationSteps.length,
                  })
                : t('keisuke.survival.debugAbsence')}
              , groupNames:{' '}
              {logRankTest.groupNames
                ? t('keisuke.survival.debugPresence', {
                    count: logRankTest.groupNames.length,
                  })
                : t('keisuke.survival.debugAbsence')}
            </small>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const steps = logRankTest.calculationSteps;
  const groupNames = logRankTest.groupNames;
  const finalObserved = logRankTest.finalObserved || [];
  const finalExpected = logRankTest.finalExpected || [];
  const finalVariance = logRankTest.finalVariance || [];

  // 各時点の詳細を折りたたみ可能にするための状態
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const toggleStep = (index: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSteps(newExpanded);
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <span>{t('keisuke.survival.calculationSteps')}</span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <>
                <i className="fas fa-chevron-up me-1"></i>
                {t('common.hide')}
              </>
            ) : (
              <>
                <i className="fas fa-chevron-down me-1"></i>
                {t('common.show')}
              </>
            )}
          </Button>
        </div>
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          {/* 最終的な統計量とカイ二乗統計量の計算を最初に表示 */}
          <div className="mb-4">
            <h6 className="mb-3">
              {t('keisuke.survival.finalStatistics')}
            </h6>
            <Table striped bordered hover size="sm" className="mb-3">
              <thead>
                <tr>
                  <th>{t('keisuke.survival.group')}</th>
                  <th>{t('keisuke.survival.observed')}</th>
                  <th>{t('keisuke.survival.expected')}</th>
                  <th>{t('keisuke.survival.variance')}</th>
                  <th>O - E</th>
                </tr>
              </thead>
              <tbody>
                {groupNames.map((groupName, groupIndex) => {
                  const diff = finalObserved[groupIndex] - finalExpected[groupIndex];
                  return (
                    <tr key={groupIndex}>
                      <td>
                        <strong>{groupName}</strong>
                      </td>
                      <td>{finalObserved[groupIndex]}</td>
                      <td>{finalExpected[groupIndex].toFixed(4)}</td>
                      <td>{finalVariance[groupIndex].toFixed(4)}</td>
                      <td style={{ color: diff > 0 ? '#dc3545' : '#28a745', fontWeight: 'bold' }}>
                        {diff > 0 ? '+' : ''}
                        {diff.toFixed(4)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {/* カイ二乗統計量の計算 */}
            {groupNames.length >= 2 && (
              <div className="p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '0.25rem' }}>
                <strong className="mb-2 d-block">
                  {t('keisuke.survival.chiSquareCalculation')}
                </strong>

                {/* 記号の説明 */}
                <div
                  className="mb-3"
                  style={{
                    fontSize: '0.9rem',
                    backgroundColor: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '0.25rem',
                  }}
                >
                  <strong className="d-block mb-2">
                    {t('keisuke.survival.symbolExplanation')}:
                  </strong>
                  <div className="ms-2">
                    <div>
                      {t('keisuke.survival.symbolObserved', {
                        group: groupNames[0],
                        value: finalObserved[0],
                      })}
                    </div>
                    <div>
                      {t('keisuke.survival.symbolExpected', {
                        group: groupNames[0],
                        value: finalExpected[0].toFixed(4),
                      })}
                    </div>
                    {groupNames.length === 2 && finalVariance[0] > 0 && (
                      <div>
                        {t('keisuke.survival.symbolVariance', {
                          group: groupNames[0],
                          value: finalVariance[0].toFixed(4),
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ fontSize: '1rem', fontFamily: 'monospace' }}>
                  {groupNames.length === 2 && finalVariance[0] > 0 ? (
                    // 2群の場合
                    <>
                      <div className="mb-2">
                        <strong>
                          {t('keisuke.survival.step1DifferenceTitle')}
                        </strong>
                        <div className="ms-2 mt-1" style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                          O₁ - E₁ = {finalObserved[0]} - {finalExpected[0].toFixed(4)} ={' '}
                          <strong>{(finalObserved[0] - finalExpected[0]).toFixed(4)}</strong>
                        </div>
                        <div
                          className="ms-2 mt-1"
                          style={{ fontSize: '0.85rem', color: '#6c757d' }}
                        >
                          {finalObserved[0] > finalExpected[0]
                            ? t('keisuke.survival.step1MoreEvents', {
                                group: groupNames[0],
                                value: (finalObserved[0] - finalExpected[0]).toFixed(2),
                              })
                            : t('keisuke.survival.step1FewerEvents', {
                                group: groupNames[0],
                                value: Math.abs(finalObserved[0] - finalExpected[0]).toFixed(2),
                              })}
                        </div>
                      </div>

                      <div className="mb-2">
                        <strong>
                          {t('keisuke.survival.step2NormalizeTitle')}
                        </strong>
                        <div className="ms-2 mt-1">χ² = (O₁ - E₁)² / V₁</div>
                        <div className="ms-2 mt-1">
                          = ({finalObserved[0]} - {finalExpected[0].toFixed(4)})² /{' '}
                          {finalVariance[0].toFixed(4)}
                        </div>
                        <div className="ms-2 mt-1">
                          = {Math.pow(finalObserved[0] - finalExpected[0], 2).toFixed(4)} /{' '}
                          {finalVariance[0].toFixed(4)}
                        </div>
                        <div
                          className="ms-2 mt-1"
                          style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#dc3545' }}
                        >
                          = <strong>{logRankTest.chiSquare.toFixed(4)}</strong>
                        </div>
                        <div
                          className="ms-2 mt-1"
                          style={{ fontSize: '0.85rem', color: '#6c757d' }}
                        >
                          {t('keisuke.survival.step2NormalizeNote')}
                        </div>
                      </div>
                    </>
                  ) : (
                    // 3群以上の場合: Mantel–Cox log-rank test
                    <>
                      <div className="mb-2">
                        <strong>
                          {t('keisuke.survival.step1GroupDifferenceTitle')}
                        </strong>
                        <div className="ms-2 mt-1" style={{ fontSize: '0.9rem' }}>
                          {groupNames.map((groupName, i) => {
                            const diff = finalObserved[i] - finalExpected[i];
                            return (
                              <div key={i}>
                                {groupName}: O - E = {finalObserved[i]} -{' '}
                                {finalExpected[i].toFixed(4)} = {diff > 0 ? '+' : ''}
                                {diff.toFixed(4)}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="mb-2">
                        <strong>
                          {t('keisuke.survival.step2LogRankTitle')}
                        </strong>
                        <div
                          className="ms-2 mt-1"
                          style={{ fontSize: '0.9rem', color: '#6c757d', fontStyle: 'italic' }}
                        >
                          {t('keisuke.survival.step2LogRankNote')}
                        </div>
                        <div
                          className="ms-2 mt-1"
                          style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#dc3545' }}
                        >
                          χ² = <strong>{logRankTest.chiSquare.toFixed(4)}</strong>
                        </div>
                        <div
                          className="ms-2 mt-1"
                          style={{ fontSize: '0.85rem', color: '#6c757d' }}
                        >
                          {t('keisuke.survival.step2LogRankSummary')}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="mt-3 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                    <strong>
                      {t('keisuke.survival.step3PValueTitle')}
                    </strong>
                    <div className="ms-2 mt-1">
                      <strong>{t('keisuke.survival.df')}:</strong> df ={' '}
                      {groupNames.length} - 1 = <strong>{logRankTest.df}</strong>
                      <div className="ms-2 mt-1" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {t('keisuke.survival.dfExplanation')}
                      </div>
                    </div>
                    <div className="ms-2 mt-2">
                      <strong>{t('keisuke.survival.pValue')}:</strong>{' '}
                      <strong style={{ color: logRankTest.pValue < 0.05 ? '#dc3545' : '#28a745' }}>
                        {logRankTest.pValue < 0.001 ? '< 0.001' : logRankTest.pValue.toFixed(4)}
                      </strong>
                      <div className="ms-2 mt-1" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {t('keisuke.survival.pValueExplanation')}
                        {logRankTest.pValue < 0.05 && (
                          <div className="mt-1" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                            {t('keisuke.survival.pValueSignificant')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 各時点での計算過程（折りたたみ可能） */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                {t('keisuke.survival.stepByStepCalculation')}
              </h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (expandedSteps.size === steps.length) {
                    setExpandedSteps(new Set());
                  } else {
                    setExpandedSteps(new Set(steps.map((_, i) => i)));
                  }
                }}
              >
                {expandedSteps.size === steps.length
                  ? t('keisuke.survival.collapseAll')
                  : t('keisuke.survival.expandAll')}
              </Button>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {steps.map((step, index) => {
                const isStepExpanded = expandedSteps.has(index);
                return (
                  <Card key={index} className="mb-2" style={{ fontSize: '0.9rem' }}>
                    <Card.Header
                      style={{ backgroundColor: '#f8f9fa', padding: '0.5rem', cursor: 'pointer' }}
                      onClick={() => toggleStep(index)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <strong>
                          {t('keisuke.survival.timePoint')}: {step.time}
                        </strong>
                        <small className="text-muted">
                          {step.totalEvents}{' '}
                          {t('keisuke.survival.events')}
                          <i
                            className={`fas fa-chevron-${isStepExpanded ? 'up' : 'down'} ms-2`}
                          ></i>
                        </small>
                      </div>
                    </Card.Header>
                    <Collapse in={isStepExpanded}>
                      <Card.Body style={{ padding: '0.75rem' }}>
                        <Table striped bordered hover size="sm" className="mb-2">
                          <thead>
                            <tr>
                              <th>{t('keisuke.survival.group')}</th>
                              <th>
                                {t('keisuke.survival.atRisk')}
                              </th>
                              <th>
                                {t('keisuke.survival.events')}
                              </th>
                              <th>{t('keisuke.survival.expected')}</th>
                              <th>{t('keisuke.survival.variance')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupNames.map((groupName, groupIndex) => (
                              <tr key={groupIndex}>
                                <td>
                                  <strong>{groupName}</strong>
                                </td>
                                <td>{step.groupAtRisk[groupIndex]}</td>
                                <td>{step.groupEvents[groupIndex]}</td>
                                <td>{step.groupExpected[groupIndex].toFixed(4)}</td>
                                <td>{step.groupVariance[groupIndex].toFixed(4)}</td>
                              </tr>
                            ))}
                            <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                              <td>{t('keisuke.survival.total')}</td>
                              <td>{step.totalAtRisk}</td>
                              <td>{step.totalEvents}</td>
                              <td colSpan={2}>-</td>
                            </tr>
                          </tbody>
                        </Table>

                        {/* 期待値の計算式（簡潔に） */}
                        <div className="mt-2" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          <strong>
                            {t('keisuke.survival.expectedFormula')}
                            :
                          </strong>
                          {groupNames.map((groupName, groupIndex) => (
                            <div key={groupIndex} className="ms-2">
                              E({groupName}) = ({step.groupAtRisk[groupIndex]} × {step.totalEvents})
                              / {step.totalAtRisk} = {step.groupExpected[groupIndex].toFixed(4)}
                            </div>
                          ))}
                        </div>

                        {/* 累積値（簡潔に） */}
                        <div className="mt-2">
                          <strong>
                            {t('keisuke.survival.cumulative')}:
                          </strong>
                          <div className="ms-2 mt-1" style={{ fontSize: '0.85rem' }}>
                            {groupNames.map((groupName, groupIndex) => (
                              <div key={groupIndex}>
                                {groupName}: O={step.cumulativeObserved[groupIndex]}, E=
                                {step.cumulativeExpected[groupIndex].toFixed(4)}, V=
                                {step.cumulativeVariance[groupIndex].toFixed(4)}
                              </div>
                            ))}
                          </div>
                        </div>
                      </Card.Body>
                    </Collapse>
                  </Card>
                );
              })}
            </div>
          </div>
        </Card.Body>
      </Collapse>
    </Card>
  );
}
